import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormGroupDirective, NgForm, UntypedFormControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UserInfoService } from '../../services/user-info.service';
import { Question } from '../interfaces/security-ques.interface';
import { CustomValidators } from 'src/app/shared/directives/custom-validators';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { NormalizedResponse } from 'src/app/shared/interfaces/http-requests/internal/response.interface';
import { UserInfoStateStructure, LOGIN_INFO_REDUCER_NAME, LoginVersion, PASSWORD_ERROR_KEYS, setBasicUserInfo } from '@ptl/account-management';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { BrandingConfigurationService } from '@ptl/brand';
import { GlobalShadowStyleDirective } from '@ptl/global-components';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return (control && control.invalid);
  }
}
@Component({
    selector: 'app-password-reset',
    templateUrl: './password-reset.component.html',
    styleUrls: ['./password-reset.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})


export class PasswordResetComponent extends GlobalShadowStyleDirective implements OnInit {
  resetForm: UntypedFormGroup;
  securityQues: Question[];
  matcher = new MyErrorStateMatcher();
  isSecurityAnswerVisible = false;
  isLoading = false;
  loginInfo:UserInfoStateStructure;
  showSecurityQues = false;

  constructor(
    private brandService: BrandingConfigurationService,
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<PasswordResetComponent>,
    private userInfoService: UserInfoService,
    private reducerUtilityService: ReducerUtilityService,
    private ngRedux: NgRedux<StateStructure>,
    private httpClient: HTTPWrapperContext
  ) {
    super();
    
     this.loginInfo  = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    this.resetForm = this.fb.group({
      currentPassword: this.fb.control(null, Validators.required),
      newPassword: this.fb.control(null),
      reenterNewPassword: this.fb.control(null)
    },
    {
      validators: [
        // check whether password contains common word.
        CustomValidators.newPasswordDomainAndUserNameValidator(this.loginInfo.basicUserInfo),
        // check whether our password and confirm password match
        CustomValidators.currPwdValidator(),
        // check if the current password is the same as the new password
        CustomValidators.oldPasswordDiffNewPasswordValidator(),   
      ]
    });
  }

  ngOnInit() {
    let region = this.brandService.getCurrentBrand().region;
    if (region === 'ND' || region === 'WY') {
      this.showSecurityQues = true;
      this.resetForm.addControl('securityQue', this.fb.control(null, [Validators.required]));
      this.resetForm.addControl('securityAnswer', this.fb.control(null, [Validators.required,
        Validators.minLength(8),
        Validators.maxLength(16),
        CustomValidators.noCommonSecurityAnswers(this.loginInfo)]));
    }
    this.setSecurityQues();
  }

  async submit(form: UntypedFormGroup) {
    if (form.valid) {
      this.isLoading = true;

      try {
        const loginVersionResponse: NormalizedResponse<LoginVersion> = await this.getLoginVersion();
        const loginInfo: UserInfoStateStructure  = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);

        const payload = {
          baseMessage: {
            headerVersion: '1.0',
            payloadVersion: '2.0',
            additionalInfo: null
          },
          payload: {
            username: loginInfo.userId.toUpperCase(),
            oldPassword: loginVersionResponse.payload.convertCase ? form.value.currentPassword.toUpperCase() : form.value.currentPassword,
            newPassword: form.value.newPassword,
            newSecurityQuestion: form.value.securityQue,
            newSecurityAnswer: form.value.securityAnswer
          }
        };

        const response = await this.userInfoService.submitPwdResetRequest(payload);
        
        // update password flag to reflect this is no longer a temp password
        const userInfoState: UserInfoStateStructure = this.reducerUtilityService.getApplicationStates()[LOGIN_INFO_REDUCER_NAME];
        userInfoState.basicUserInfo.temppw = false;
        this.ngRedux.dispatch(setBasicUserInfo(userInfoState.basicUserInfo));

        this.closeModal();
      }
      catch (e) {
        const error: HttpErrorResponse = e as HttpErrorResponse;
        const normalizedError: NormalizedResponse<any> = error.error;
        if (
          normalizedError &&
          normalizedError.baseMessage &&
          normalizedError.baseMessage.returnCodeDescription === 'GEN-INVCURPWD'
        ) {
          this.resetForm.controls.currentPassword.setErrors({[PASSWORD_ERROR_KEYS.CURRENT_PASSWORD_DOES_NOT_MATCH]: true});
        }
      }

      this.isLoading = false;
    }
  }

  closeModal() {
    this.dialogRef.close();
  }
  async setSecurityQues() {
    try {
      const response = await this.userInfoService.getSecurityQues();
      this.securityQues = response.payload.questionList;
    } catch (e) {
    }
  }

  async getLoginVersion(): Promise<NormalizedResponse<LoginVersion>> {
    const loginInfo: UserInfoStateStructure  = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const serviceUrl = `/rbsmbl/x-services/userauth/v1/loginversion/`;
    

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: serviceUrl,
      headers: new HttpHeaders({
        FromDP: loginInfo.userId
      }),
      withCredentials: true
    });

    // RBSMBL services not moved into axway, yet
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest<NormalizedResponse<LoginVersion>>(requestConfig);
  }

}
